import React, { useEffect, useState } from 'react';

import useUrlManager from 'lib/hooks/coveo/useUrlManager';
import headlessEngine from 'lib/utils/coveo/headlessEngine';
import useStandaloneSearchData from 'lib/hooks/coveo/useStandaloneSearchData';
import useQuerySummary from 'lib/hooks/coveo/useQuerySummary';
import {
  facetManager,
  studyTypeFacet,
  phaseFacet,
  genderFacet,
  relatedTermsFacet,
} from 'lib/utils/coveo/headlessFacets';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import Grid from '@mui/material/Grid';
import ConditionFilter from './ConditionFilter';
import SortByRelevanceFilter from './SortByRelevanceFilter';
import EnrollingSiteFilter from './EnrollingSiteFilter';
import LocationFilter from './LocationFilter';
import ParticipantDetailsFilter from './ParticipantDetailsFilter';
import StudyPhaseFilter from './StudyPhaseFilter';
import RelatedTermsFilter from './RelatedTermsFilter';
import StudyTypeFilter from './StudyTypeFilter';
import { Text } from '@sitecore-jss/sitecore-jss-nextjs';
import FacetManager from '../CR/FacetManager';
import { enrollingFacet } from 'lib/utils/coveo/headlessFacets';
import UiHeading from '../shared/Heading';
import UiBox from '../shared/Box';
import ButtonDiscreet from 'components/shared/Button/ButtonDiscreet';

const SearchTrialFilter = (props: any): JSX.Element => {
  const theme = useTheme();
  const isTabletOrMobile = useMediaQuery(theme.breakpoints.down(960));
  const [showAllFilter, setShowAllFilter] = useState(false);
  useUrlManager(headlessEngine);
  const totalResults = useQuerySummary();

  const [searchResultsText, setSearchResultsText] = useState<string>('');
  const [timeoutSearchResultText, setTimeoutSearchResultText] = useState<boolean>(false);

  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout> | undefined;

    if (!timeoutSearchResultText && totalResults <= 0) {
      timeoutId = setTimeout(() => {
        setTimeoutSearchResultText(true);
      }, 500);
    } else {
      setSearchResultsText(
        `${props?.fields?.filterText?.value.replace('{count}', totalResults)}` ||
          `Filter ${totalResults} Search Results`
      );
    }

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [totalResults]);

  const standaloneData = useStandaloneSearchData(headlessEngine);

  const showMoreText = props?.fields?.showMoreText?.value || 'Show More';
  const showLessText = props?.fields?.showLessText?.value || 'Show Less';
  const clearAllText = props?.fields?.clearAllText?.value || 'Clear all x';
  const noFacetsText = props?.fields?.noFacetsText?.value || 'No facets';

  const filterSitecoreData: any = {};
  props?.fields?.filterFields?.forEach((item: any) => {
    filterSitecoreData[item?.fields?.sectionKey?.value] = {
      key: item?.fields?.sectionKey?.value,
      heading: item?.fields?.heading,
      data: item?.fields?.filterSection,
    };
  });

  const FILTER_KEYS = {
    CONDITION: 'condition',
    SORT_BY: 'SortBy',
    LOCATION: 'location',
    PARTICIPANT_DETAILS: 'participantDetails',
    PHASE: 'phase',
    STUDY_TYPE: 'studyType',
    RELATED_TERMS: 'relatedItems',
    SHOW_CURRENT_SITE: 'Show Current site',
  };

  return (
    <Grid
      container
      sx={{
        background: !isTabletOrMobile
          ? (theme.palette as any)?.backgroundGradientColor6.main
          : 'none',
        padding: !isTabletOrMobile ? '1rem' : 'none',
        height: '100%',
      }}
    >
      <Grid
        item
        container
        sx={{
          background: theme.palette.backgroundColorType3.main,
          borderRadius: '1rem',
          padding: isTabletOrMobile ? '1rem' : '2rem',
          marginBottom: '1rem',
        }}
        direction={'column'}
        gap={1}
      >
        <Grid item>
          <UiBox styles={{ paddingBottom: '10px' }}>
            <UiHeading size="h2">{searchResultsText}</UiHeading>
          </UiBox>
        </Grid>
        <ConditionFilter
          fields={filterSitecoreData[FILTER_KEYS.CONDITION]}
          data={standaloneData}
          coiInputTextLimit={props?.fields?.coiInputTextLimit?.value || 50}
          lengthToSuggest={props?.fields?.lengthToSuggestion?.value || 3}
          isRequired={props?.fields?.isCOIRequired?.value === true ? true : false}
          requiredMessage={props?.fields?.coiRequiredErrorMessage?.value || 'Invalid condition'}
        />

        <FacetManager controller={facetManager}>
          <EnrollingSiteFilter
            controller={enrollingFacet}
            fields={filterSitecoreData[FILTER_KEYS.SHOW_CURRENT_SITE]}
          />
          {!isTabletOrMobile || showAllFilter ? (
            <>
              <SortByRelevanceFilter fields={filterSitecoreData[FILTER_KEYS.SORT_BY]} />
              <LocationFilter
                fields={filterSitecoreData[FILTER_KEYS.LOCATION]}
                zipcodeRegex={props?.fields?.zipcodeRegex}
                zipcodeRegexFailedErrorMessage={props?.fields?.zipcodeRegexFailedErrorMessage}
                zipcodeRequiredErrorMessage={props?.fields?.zipcodeRequiredErrorMessage}
                isZipcodeRequired={props?.fields?.isZipcodeRequired}
              />
              <ParticipantDetailsFilter
                fields={filterSitecoreData[FILTER_KEYS.PARTICIPANT_DETAILS]}
                controller={genderFacet}
              />
              <StudyPhaseFilter
                fields={filterSitecoreData[FILTER_KEYS.PHASE]}
                controller={phaseFacet}
                showMoreText={showMoreText}
                showLessText={showLessText}
                clearAllText={clearAllText}
                noFacetsText={noFacetsText}
              />
              <StudyTypeFilter
                fields={filterSitecoreData[FILTER_KEYS.STUDY_TYPE]}
                controller={studyTypeFacet}
                showMoreText={showMoreText}
                showLessText={showLessText}
                clearAllText={clearAllText}
                noFacetsText={noFacetsText}
              />
              <RelatedTermsFilter
                fields={filterSitecoreData[FILTER_KEYS.RELATED_TERMS]}
                controller={relatedTermsFacet}
                showMoreText={showMoreText}
                showLessText={showLessText}
                clearAllText={clearAllText}
                noFacetsText={noFacetsText}
              />
            </>
          ) : (
            <ButtonDiscreet id="show-more-filters" onClick={() => setShowAllFilter(true)}>
              <Text field={props?.fields?.showMoreFilterText} />
            </ButtonDiscreet>
          )}
        </FacetManager>
      </Grid>
    </Grid>
  );
};

export default SearchTrialFilter;
